import { Observable } from 'rxjs';
import { DbService } from './../../services/db.service';
import { inject, Injectable } from '@angular/core';
import { CatalogRecord, initCatalogRecord, prepareCatalogRecord } from 'shared';
import { GalleryRecord } from 'shared';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { limit, orderBy, QueryConstraint, startAfter, where } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root'
})
export class CatalogService {
  private _functions = inject(Functions);

  constructor(private dbService: DbService) {}

  createCatalogRecord(record: CatalogRecord): Promise<CatalogRecord> {
    return this.dbService.create<CatalogRecord>(prepareCatalogRecord(record), record.id);
  }

  getCatalogRecord(id: string): Promise<CatalogRecord> {
    return this.dbService.read(id, 'catalog-records');
  }

  batchCreateCatalogRecords(records: CatalogRecord[]): Promise<boolean> {
    return this.dbService.batchCreate(
      records.map((record) => {
        return prepareCatalogRecord(record);
      })
    );
  }

  batchUpdateCatalogRecords(records): Promise<void> {
    return this.dbService.batchUpdate(
      records.map((record) => {
        return prepareCatalogRecord(record, true);
      })
    );
  }

  setCatalogRecordPrices(retail, wholesale, resetWholesaleCatalogFlag = false): Promise<number> {
    const payload = {
      retail,
      wholesale,
      resetWholesaleCatalogFlag
    };
    return httpsCallable<any, number>(this._functions, 'resetCatalogPrices', { timeout: 300000 })(
      payload
    ).then((r) => {
      return r.data;
    });
  }

  updateCatalogRecordsBulk(filePath: string, dryRun: boolean): Promise<{ [key: string]: number }> {
    const payload = {
      filePath,
      dryRun
    };
    return httpsCallable<any, { [key: string]: number }>(
      this._functions,
      'updateCatalogRecordsBulk',
      { timeout: 300000 }
    )(payload).then((r) => {
      return r.data;
    });
  }

  exportCatalogRecordsToCSV() {
    const payload = {
      collection: 'catalog-records'
    };
    return httpsCallable<any, any>(
      this._functions,
      'exportCollection'
    )(payload).then((r) => {
      return r.data;
    });
  }

  updateCatalogRecord(record: CatalogRecord): Promise<CatalogRecord> {
    return this.dbService.update<CatalogRecord>(prepareCatalogRecord(record));
  }

  deleteCatalogRecord(record: CatalogRecord): Promise<void> {
    return this.dbService.delete<CatalogRecord>(record);
  }

  getCatalogRecords(
    searchTerm: string = '',
    startAfterInput: any = ''
  ): Observable<CatalogRecord[]> {
    const constraints: QueryConstraint[] = [orderBy('id', 'asc')];
    if (searchTerm) {
      constraints.push(where('searchTerms', 'array-contains', searchTerm.toLowerCase()));
    }
    if (startAfterInput) {
      constraints.push(startAfter(startAfterInput));
    }
    constraints.push(limit(10));
    return this.dbService.query$<CatalogRecord>('catalog-records', constraints, initCatalogRecord);
  }

  getAllCatalogRecords(): Observable<CatalogRecord[]> {
    return this.dbService.query$<CatalogRecord>('catalog-records', undefined, initCatalogRecord);
  }

  getGalleryCatalogRecords(galleryRecordId: string) {
    return this.dbService.queryCollection$<CatalogRecord>(
      'catalog-records',
      [where('galleryRecordId', '==', galleryRecordId), limit(20)],
      initCatalogRecord
    );
  }

  async linkCatalogRecordToGallery(catalogRecord: CatalogRecord, galleryRecord: GalleryRecord) {
    const catalogId = catalogRecord.id;
    galleryRecord.catalogRecords.push(catalogId);
    if (catalogRecord.inCatalog) {
      galleryRecord.activeCatalogRecords.push(catalogId);
    }
    galleryRecord.searchTerms.push(catalogId);

    catalogRecord.galleryRecordId = galleryRecord.id;
    catalogRecord.category = galleryRecord.category;
    catalogRecord.categorySearch = galleryRecord.categorySearch;

    return this.dbService.transaction(async (transaction) => {
      const catRef = this.dbService.getRef('catalog-records', catalogId);
      const gallRef = this.dbService.getRef('gallery-records', galleryRecord.id);
      transaction.update(catRef, catalogRecord as {});
      transaction.update(gallRef, galleryRecord as {});
    });
  }
}
